import { Context } from '@apollo/client';

import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import {
  GET_PRICING_INFO,
  GET_DYNAMIC_PRICING_ACCOUNTS,
  GET_DYNAMIC_PRICING_JOIN,
  GET_DYNAMIC_PRICING_BUY_MEMBERSHIP,
} from 'approot/shared/graphql/pricing/pricing.graphql';
import type { PricingInfoQuery } from './pricing.graphql.generated';
import type {
  DynamicPricingAccountsQuery,
  DynamicPricingAccountsQueryVariables,
} from './pricing.graphql.generated';
import type {
  DynamicPricingJoinQuery,
  DynamicPricingJoinQueryVariables,
} from './pricing.graphql.generated';
import { GST_TAX, SALES_TAX } from 'global/constants';
import type {
  DynamicPricingBuyMembershipQuery,
  DynamicPricingBuyMembershipQueryVariables,
} from './pricing.graphql.generated';
import type { DynamicPricingFragment } from './pricing.graphql.generated';
import useUserData from 'approot/shared/user/user-data';

export const usePricingInfo = () => {
  const { isStudentSignedIn } = useUserData();

  const query = useLoggingQuery<PricingInfoQuery, null>(
    GET_PRICING_INFO,
    {
      fetchPolicy: 'cache-first',
      skip: isStudentSignedIn,
    },
    false
  );

  return query;
};

type DynamicPricingJoinProps = {
  variables: DynamicPricingJoinQueryVariables;
  context?: Context;
  skip?: boolean;
  onCompleted?: (data: DynamicPricingJoinQuery) => void;
  authenticate?: boolean;
};

export const usePricingJoin = ({
  variables,
  context,
  skip,
  onCompleted,
  authenticate = false,
}: DynamicPricingJoinProps) => {
  const query = useLoggingQuery<
    DynamicPricingJoinQuery,
    DynamicPricingJoinQueryVariables
  >(
    GET_DYNAMIC_PRICING_JOIN,
    {
      variables,
      context,
      fetchPolicy: 'no-cache',
      skip,
      onCompleted,
    },
    authenticate
  );

  return {
    ...query,
    ...getPricingValues(
      variables.input.isWholeSchool,
      query.data?.dynamicPricingJoin
    ),
  };
};

type DynamicPricingAccountsProps = {
  variables: DynamicPricingAccountsQueryVariables;
  skip?: boolean;
  onCompleted?: (data: DynamicPricingAccountsQuery) => void;
};

export const usePricingAccounts = ({
  variables,
  skip,
  onCompleted,
}: DynamicPricingAccountsProps) => {
  const query = useLoggingQuery<
    DynamicPricingAccountsQuery,
    DynamicPricingAccountsQueryVariables
  >(GET_DYNAMIC_PRICING_ACCOUNTS, {
    variables,
    fetchPolicy: 'no-cache',
    skip,
    onCompleted,
  });

  return {
    ...query,
    ...getPricingValues(
      variables.input.isWholeSchool,
      query.data?.dynamicPricingAccount
    ),
  };
};

type DyanmicPricingBuyProps = {
  variables: DynamicPricingBuyMembershipQueryVariables;
  skip?: boolean;
  onCompleted?: (data: DynamicPricingBuyMembershipQuery) => void;
};

export const usePricingBuy = ({
  variables,
  skip,
  onCompleted,
}: DyanmicPricingBuyProps) => {
  const query = useLoggingQuery<
    DynamicPricingBuyMembershipQuery,
    DynamicPricingBuyMembershipQueryVariables
  >(GET_DYNAMIC_PRICING_BUY_MEMBERSHIP, {
    variables,
    fetchPolicy: 'no-cache',
    skip,
    onCompleted,
  });

  return {
    ...query,
    ...getPricingValues(
      variables.input.isWholeSchool,
      query.data?.dynamicPricingBuyMembership
    ),
  };
};

function getPricingValues(
  isWholeSchool: boolean,
  data?: DynamicPricingFragment
) {
  const salesTax = data?.totalTax || 0;
  const creditApplied = data?.creditUsed || 0;
  const total = data?.total || 0;

  return {
    costPerTeacher:
      (isWholeSchool ? data?.wholeSchoolPrice : data?.standardPrice) || 0,
    total,
    salesTax,
    includesGst: data?.taxCategory === GST_TAX,
    includesSalesTax: data?.taxCategory === SALES_TAX && salesTax > 0,
    fee: data?.fee || 0,
    promoCodeError: data?.isValidPromoCode === false,
    percentOff: data?.percentOff,
    discountApplied: data?.discountApplied,
    creditApplied,
    creditRemaining: data?.creditRemaining || 0,
    isCreditsPurchase: creditApplied > 0 && total === 0,
  };
}
